import { Navigate, RouteObject } from 'react-router-dom';
import { LegacyBillingRedirect } from './LegacyBillingRedirect';
import { PracticeIndexRedirect } from './PracticeIndexRedirect';
import { AuthenticationCallbackWrapper } from '../Views/AuthenticationCallback';
import { DashboardPage } from '@features/dashboard';
import { RootErrorBoundary } from '../components/errors/RootErrorBoundary';
import { EditNoteTemplate } from '@features/notes';
import NoteJotDocView from '../features/notes/routes/NoteJotDocView';
import UserView from '../Views/UserView';
import UserNotesView from '../Views/UserNotesView';
import { NoteProcessingView } from '../Views/NoteProcessingView';
import { AdminViewOnly } from '../Views/Admin/AdminViewOnly';
import { TranscribeView } from '../Views/Admin/TranscribeView';
import ViewTemplate from '../Views/Shared/ViewTemplate';
import ViewUserTemplates from '../Views/Shared/ViewUserTemplates';
import { PracticeAppointmentTypesPage } from '@features/appointment-types';
import {
  CliniciansPage,
  DataSecurityPage,
  OrganizationManagementPage,
  PracticeSettingsPage,
} from '@features/organizations';
import { AddOnBundlesPage, SubscriptionsPage } from '@features/subscriptions';
import { BillingMigrationSessionDetail, BillingSettingsPage } from '@features/billing-readiness';
import { PayerDocsCenterPage } from '@features/payer-docs-center';
import { RecorderView } from '../Views/RecorderView';
import { TestNoteRecordingView } from '../Views/TestNoteRecordingView';
import { PatientsView } from '../Views/PatientsView';
import { NoteRoute } from './NoteRoute';
import { PatientPageRoute } from './PatientPageRoute';
import CheckoutSuccess from '../Views/CheckoutSuccess';
import CheckoutCancel from '../Views/CheckoutCancel';
import { EncounterPage } from '@features/encounters';
import { ScheduleView } from '@features/appointments';
import { DocumentsPage, LabsPage, FormsPage, DocumentTemplatesPage } from '@features/documents';
import FormDetailFullView from '../Views/FormDetailFullView';
import TemplateBuilderView from '../components/TemplateBuilder/TemplateBuilderView';
import { TemplatesPage } from '@features/templates';
import { OnboardingView } from '@features/onboarding';
import { AuthenticationSignupWrapper } from '../Views/Admin/UserSignup';
import { AuthenticatedLayout } from './layouts/AuthenticatedLayout';
import { ActionsBarLayout } from './layouts/ActionsBarLayout';
import { PracticeLayout } from './layouts/PracticeLayout';
import { Auth0ProviderWithNavigate } from '../auth0-provider';
import GoogleAuthCallback from '../Views/GoogleAuthCallback';
import { GoogleAuthCallbackWrapper } from '../Views/GoogleAuthCallbackWrapper';
import { DiagnosticsView } from '@views/DiagnosticsView';
import { RecordingPermissionGate } from '../components/guards/RecordingPermissionGate';
import { BillInsurancePage } from '@features/billing';
import { CoachPage } from '@features/jotcoach';
import {
  JotAuditPageWrapper,
  ProviderDetail,
  NoteAuditDetail,
  RuleAnalyticsPage,
  EncounterAuditsList,
  EncounterAuditDetail,
} from '@features/jotaudit';
import { SentDocumentsPage } from '@features/documents';
import { PatientImportListPage, PatientImportSessionPage } from '@features/patient-import';
import { PayerSearchPage } from '@features/payer-search';
import { MergeCustomizationPage, PatientDeduplicationPage } from '@features/patient-deduplication';

export const mainRoutes: RouteObject[] = [
  { path: '/callback', element: <AuthenticationCallbackWrapper /> },
  {
    path: '/google-auth-callback',
    element: <GoogleAuthCallbackWrapper />,
  },
  {
    path: '/signup',
    element: <AuthenticationSignupWrapper />,
  },
  {
    path: '/accept-invitation',
    async lazy() {
      const { AcceptInvitationPage } = await import('@features/accept-invitation');
      return { Component: AcceptInvitationPage };
    },
  },
  {
    path: '/share/templates/:templateId',
    element: <ViewTemplate />,
  },
  {
    path: '/share/templates/created-by/:userId',
    element: <ViewUserTemplates />,
  },
  {
    path: '/',
    element: <AuthenticatedLayout />,
    errorElement: (
      <Auth0ProviderWithNavigate>
        <RootErrorBoundary />
      </Auth0ProviderWithNavigate>
    ),
    children: [
      {
        path: '/',
        index: true,
        element: <DashboardPage />,
      },
      {
        path: '/dashboard',
        element: <DashboardPage />,
      },
      {
        path: '/templatebuilder',
        element: <TemplateBuilderView />,
      },
      {
        path: '/billing',
        element: <Navigate to="/jotbill" replace />,
      },
      {
        path: '/jotbill',
        async lazy() {
          const { BillingV2 } = await import('@features/billing/components/v2');
          return { Component: BillingV2 };
        },
      },
      {
        path: '/jotbill/claim/:claimId',
        async lazy() {
          const { IntelligentClaimView } = await import('@features/billing/components/ClaimDetail');
          return { Component: IntelligentClaimView };
        },
      },
      {
        path: '/jotbill/claim/:claimId/edit',
        async lazy() {
          const { IntelligentClaimEdit } = await import('@features/billing/components/ClaimDetail');
          return { Component: IntelligentClaimEdit };
        },
      },
      {
        path: '/jotbill/encounter/:encounterId',
        async lazy() {
          const { IntelligentEncounterDetail } = await import('@features/billing/components/EncounterDetail');
          return { Component: IntelligentEncounterDetail };
        },
      },
      {
        path: '/jotbill/encounter/:encounterId/cms',
        async lazy() {
          const { EncounterCms1500Route } = await import('@features/billing/components/EncounterDetail');
          return { Component: EncounterCms1500Route };
        },
      },
      {
        path: '/bill-insurance',
        element: <BillInsurancePage />,
      },
      {
        path: '/reports-analytics',
        async lazy() {
          const { ReportsHomePage } = await import('@features/reports');
          return { Component: ReportsHomePage };
        },
      },
      {
        path: '/reports-analytics/:reportKey',
        async lazy() {
          const { ReportRunPage } = await import('@features/reports');
          return { Component: ReportRunPage };
        },
      },
      {
        path: '/sent-documents',
        element: <SentDocumentsPage />,
      },
      {
        // Legacy redirect — the worklist replaces this surface as the
        // "Verifications" tab inside JotBill (GDN-1338). Keep one cycle,
        // then drop.
        path: '/eligibility-checks',
        element: <Navigate to="/jotbill?tab=verifications" replace />,
      },
      {
        path: '/payer-search',
        element: <PayerSearchPage />,
      },
      {
        path: '/intake',
        async lazy() {
          const { IntakeHubPage } = await import('@features/intake-hub');
          return { Component: IntakeHubPage };
        },
      },
      {
        path: '/jotcoach',
        element: <CoachPage />,
      },
      {
        path: '/jotaudit/rules/:ruleId/analytics',
        element: <RuleAnalyticsPage />,
      },
      {
        path: '/jotaudit/provider/:userId',
        element: <ProviderDetail />,
      },
      {
        path: '/jotaudit/note/:noteId',
        element: <NoteAuditDetail />,
      },
      {
        path: '/jotaudit/encounters',
        element: <EncounterAuditsList />,
      },
      {
        path: '/jotaudit/encounter/:encounterId',
        element: <EncounterAuditDetail />,
      },
      {
        path: '/jotaudit',
        element: <JotAuditPageWrapper />,
      },
      // Rule Build routes
      {
        path: '/rule-build',
        async lazy() {
          const { RuleBuildPage } = await import('@features/rule-build');
          return { Component: RuleBuildPage };
        },
      },
      {
        path: '/rule-build/:taskId',
        async lazy() {
          const { RuleBuildPage } = await import('@features/rule-build');
          return { Component: RuleBuildPage };
        },
      },
      {
        path: '/billing/claim/:claimId',
        element: <LegacyBillingRedirect path="claim" />,
      },
      {
        path: '/billing/claim/:claimId/edit',
        element: <LegacyBillingRedirect path="claim" suffix="/edit" />,
      },
      {
        path: '/billing/encounter/:encounterId',
        element: <LegacyBillingRedirect path="encounter" />,
      },
      {
        path: '/patients/import',
        element: <PatientImportListPage />,
      },
      {
        path: '/patients/import/:sessionId',
        element: <PatientImportSessionPage />,
      },
      {
        path: '/patients/',
        element: <PatientsView />,
      },
      {
        path: '/patients/merge',
        element: <PatientDeduplicationPage />,
      },
      {
        path: '/patients/merge/customize',
        element: <MergeCustomizationPage />,
      },
      {
        path: '/patient/:patientId',
        element: <PatientPageRoute />,
      },
      {
        path: '/jotrx/prescribe-launch/:patientId',
        async lazy() {
          const { PatientPrescribeLauncher } = await import(
            '@features/eprescriptions/components/PatientPrescribeLauncher'
          );
          return { Component: PatientPrescribeLauncher };
        },
      },
      {
        path: '/notes/',
        element: <UserNotesView />,
      },
      {
        path: '/templates/',
        element: <TemplatesPage />,
      },
      {
        path: '/templates/build',
        async lazy() {
          const { TemplateBuildPage } = await import('@features/templates-ai-edit');
          return { Component: TemplateBuildPage };
        },
      },
      {
        path: '/templates/build/:taskId',
        async lazy() {
          const { TemplateBuildPage } = await import('@features/templates-ai-edit');
          return { Component: TemplateBuildPage };
        },
      },
      {
        path: '/onboarding/',
        element: <OnboardingView />,
      },
      {
        path: '/record/',
        element: (
          <RecordingPermissionGate>
            <RecorderView />
          </RecordingPermissionGate>
        ),
      },
      {
        path: '/schedule',
        element: <ScheduleView />,
      },
      {
        path: '/documents',
        element: <DocumentsPage />,
      },
      {
        path: '/document-templates',
        element: <DocumentTemplatesPage />,
      },
      {
        path: '/labs',
        element: <LabsPage />,
      },
      {
        path: '/form-documents',
        element: <FormsPage />,
      },
      {
        path: '/forms',
        async lazy() {
          const { LibraryView } = await import('@features/form-library');
          return { Component: LibraryView };
        },
      },
      {
        path: '/rules',
        async lazy() {
          const { RulesManagementPage } = await import('@features/rules');
          return { Component: RulesManagementPage };
        },
      },
      {
        path: '/form-builder/:formId',
        async lazy() {
          const { BuilderPage } = await import('@features/form-builder');
          return { Component: BuilderPage };
        },
      },
      {
        path: '/encounters/:encounterId',
        element: (
          <RecordingPermissionGate>
            <EncounterPage />
          </RecordingPermissionGate>
        ),
      },
      {
        path: '/forms/:formSessionId',
        element: <FormDetailFullView />,
      },
      {
        path: '/exampleRecord/',
        element: <TestNoteRecordingView />,
      },
      {
        path: '/note/:noteId',
        element: <NoteRoute />,
        children: [
          {
            index: true,
            async lazy() {
              const { NoteChatView } = await import('@features/note-ai-edit');
              return { Component: NoteChatView };
            },
          },
          {
            path: 'edit-template',
            element: <EditNoteTemplate />,
          },
        ],
      },
      {
        path: '/jotdoc/:jotdocId',
        element: <ActionsBarLayout />,
        children: [
          {
            index: true,
            element: <NoteJotDocView />,
          },
        ],
      },
      {
        path: '/status/:noteId',
        element: <NoteProcessingView />,
      },
      {
        path: '/transcribe',
        element: (
          <AdminViewOnly>
            <TranscribeView />
          </AdminViewOnly>
        ),
      },
      {
        path: '/user',
        element: <UserView />,
      },
      {
        path: '/user/access',
        element: <Navigate to="/user?tab=my-access" replace />,
      },
      {
        path: '/group_administration',
        element: <PracticeIndexRedirect />,
      },
      {
        path: '/practice',
        element: <PracticeLayout />,
        children: [
          {
            index: true,
            element: <PracticeIndexRedirect />,
          },
          {
            path: 'clinicians',
            element: <CliniciansPage />,
          },
          {
            path: 'settings',
            element: <PracticeSettingsPage />,
          },
          {
            path: 'appointment-types',
            element: <PracticeAppointmentTypesPage />,
          },
          {
            path: 'subscriptions',
            element: <SubscriptionsPage />,
          },
          {
            path: 'data-security',
            element: <DataSecurityPage />,
          },
          {
            path: 'billing-settings',
            element: <BillingSettingsPage />,
          },
          {
            path: 'billing-data-migration/session/:sessionId',
            element: <BillingMigrationSessionDetail />,
          },
          {
            path: 'payer-docs-center',
            element: <PayerDocsCenterPage />,
          },
          {
            path: 'payments',
            element: <Navigate to="/jotbill?tab=patient-billing" replace />,
          },
        ],
      },
      {
        path: '/bundles',
        element: <AddOnBundlesPage />,
      },
      {
        path: '/checkout-success',
        element: <CheckoutSuccess />,
      },
      {
        path: '/checkout-cancel',
        element: <CheckoutCancel />,
      },
      {
        path: '/google-auth-callback',
        element: <GoogleAuthCallback />,
      },
      {
        path: '/diagnostics',
        element: <DiagnosticsView />,
      },
    ],
  },
];
